import { createSlice } from '@reduxjs/toolkit';
import { mergeReduxStateData } from 'library/helpers/reduxHelpers';

const initialState = {
	data: {},
	loading: false,
	error: null,
	timeStamp: null,
	categoriesLoadingComplete: false,
	loadingCreateCategory: false,
	loadingUpdateCategory: false,
	loadingDeleteCategory: false,
	loadCategoryFieldsLoading: false,
	loadingCreateCategoryField: false,
	loadingUpdateCategoryField: false,
	loadingDeleteCategoryField: false
};

const categoriesSlice = createSlice({
	name: 'categories',
	initialState,
	reducers: {
		// Load Categories
		loadCategories: (state) => {
			state.loading = true;
			state.error = null;
		},
		loadCategoriesSuccess: (state, action) => {
			state.timeStamp = Date.now();
			state.loading = false;

			const { orgID, data } = action.payload;
			state.data[orgID] = mergeReduxStateData(state.data[orgID] || [], data, 'categoryID');
			state.categoriesLoadingComplete = true;
		},
		loadCategoriesError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},
		// Create Default Category
		createDefaultCategory: (state) => {
			state.loadingCreateCategory = true;
		},
		// Create Category
		createCategory: (state) => {
			state.loadingCreateCategory = true;
			state.error = null;
		},
		createCategorySuccess: (state) => {
			state.loadingCreateCategory = false;
		},
		createCategoryError: (state, action) => {
			state.loadingCreateCategory = false;
			state.error = action.payload;
		},

		// Update Category
		updateCategory: (state) => {
			state.loadingUpdateCategory = true;
		},
		updateCategorySuccess: (state) => {
			state.loadingUpdateCategory = false;
		},
		updateCategoryError: (state, action) => {
			state.loadingUpdateCategory = false;
			state.error = action.payload;
		},

		// Delete Category
		deleteCategory: (state) => {
			state.loadingDeleteCategory = true;
		},
		deleteCategorySuccess: (state, action) => {
			state.loadingDeleteCategory = false;
		},
		deleteCategoryError: (state, action) => {
			state.loadingDeleteCategory = false;
			state.error = action.payload;
		},
		// Load All Category Fields
		loadAllCategoryFields(state) {
			state.loading = true;
			state.error = null;
		},
		loadAllCategoryFieldsSuccess(state, action) {
			const { orgID, data } = action.payload;

			if (state.data[orgID]) {
				state.data[orgID] = state.data[orgID].map((category) => {
					const fields = data[category.categoryID] || [];
					return { ...category, fields };
				});
			}

			state.loading = false;
		},
		loadAllCategoryFieldsError(state, action) {
			state.error = action.payload;
			state.loading = false;
		},

		// Load Category Fields
		loadCategoryFields: (state) => {
			state.loadCategoryFieldsLoading = true;
			state.error = null;
		},
		loadCategoryFieldsSuccess: (state, action) => {
			const { orgID, categoryID, fields } = action.payload;
			state.data[orgID] = state.data[orgID]?.map((category) =>
				category.categoryID === categoryID ? { ...category, fields } : category
			);
			state.loadCategoryFieldsLoading = false;
		},
		loadCategoryFieldsError: (state, action) => {
			state.loadCategoryFieldsLoading = false;
			state.error = action.payload;
		},

		// Create Category Field
		createCategoryField: (state) => {
			state.loadingCreateCategoryField = true;
		},
		createCategoryFieldSuccess: (state) => {
			state.loadingCreateCategoryField = false;
		},
		createCategoryFieldError: (state, action) => {
			state.loadingCreateCategoryField = false;
			state.error = action.payload;
		},

		// Update Category Field
		updateCategoryField: (state) => {
			state.loadingUpdateCategoryField = true;
		},
		updateCategoryFieldSuccess: (state) => {
			state.loadingUpdateCategoryField = false;
		},
		updateCategoryFieldError: (state, action) => {
			state.loadingUpdateCategoryField = false;
			state.error = action.payload;
		},

		// Delete Category Field
		deleteCategoryField: (state) => {
			state.loadingDeleteCategoryField = true;
		},
		deleteCategoryFieldSuccess: (state, action) => {
			state.loadingDeleteCategoryField = false;
		},
		deleteCategoryFieldError: (state, action) => {
			state.loadingDeleteCategoryField = false;
			state.error = action.payload;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

// Export actions
export const categoriesActions = categoriesSlice.actions;

// Export reducer
export default categoriesSlice.reducer;
